import React, { useState } from 'react';
import {
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Row,
  Col,
  Alert,
  Spinner
} from 'reactstrap';
import { signInWithEmail, createUserWithEmail, signInWithGoogle, resetPassword } from '../../firebase/auth';
import { setCookie } from '../../views/utils';
import content from '../../content.json';

const brandname = content.brandname;
const login_api_url = process.env.REACT_APP_LOGIN_API_URL;

const FirebaseAuth = ({ onAuthSuccess, onAuthError }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    displayName: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  const validateForm = () => {
    if (!formData.email || !formData.password) {
      setError('Email and password are required');
      return false;
    }

    if (!isLogin) {
      if (formData.password !== formData.confirmPassword) {
        setError('Passwords do not match');
        return false;
      }
      if (formData.password.length < 6) {
        setError('Password must be at least 6 characters');
        return false;
      }
    }

    return true;
  };

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      let result;
      if (isLogin) {
        result = await signInWithEmail(formData.email, formData.password);
      } else {
        result = await createUserWithEmail(
          formData.email, 
          formData.password, 
          formData.displayName
        );
      }

      if (result.success) {
        // Get Firebase ID token and send to your backend
        const firebaseToken = await result.user.getIdToken();
        
        // Send Firebase token to your backend for verification
        const response = await fetch(`${login_api_url}/api/FirebaseLogin`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': firebaseToken
          },
          body: JSON.stringify({
            email: result.user.email,
            displayName: result.user.displayName,
            uid: result.user.uid
          })
        });

        if (response.ok) {
          const data = await response.json();
          // Store your custom JWT token
          setCookie(`${brandname}_token`, data.token, { 
            path: '/', 
            expires: new Date(Date.now() + 1000 * 60 * 60 * 24 * 365) 
          });
          
          setSuccess(isLogin ? 'Login successful!' : 'Account created successfully!');
          if (onAuthSuccess) onAuthSuccess(data);
        } else {
          throw new Error('Backend authentication failed');
        }
      } else {
        setError(result.error);
      }
    } catch (error) {
      setError(error.message || 'Authentication failed');
      if (onAuthError) onAuthError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleAuth = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const result = await signInWithGoogle();
      
      if (result.success) {
        // Get Firebase ID token and send to your backend
        const firebaseToken = await result.user.getIdToken();
        
        // Send Firebase token to your backend for verification
        const response = await fetch(`${login_api_url}/api/FirebaseLogin`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': firebaseToken
          },
          body: JSON.stringify({
            email: result.user.email,
            displayName: result.user.displayName,
            uid: result.user.uid
          })
        });

        if (response.ok) {
          const data = await response.json();
          // Store your custom JWT token
          setCookie(`${brandname}_token`, data.token, { 
            path: '/', 
            expires: new Date(Date.now() + 1000 * 60 * 60 * 24 * 365) 
          });
          
          setSuccess('Google login successful!');
          if (onAuthSuccess) onAuthSuccess(data);
        } else {
          throw new Error('Backend authentication failed');
        }
      } else {
        setError(result.error);
      }
    } catch (error) {
      setError(error.message || 'Google authentication failed');
      if (onAuthError) onAuthError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!formData.email) {
      setError('Please enter your email address');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const result = await resetPassword(formData.email);
      if (result.success) {
        setSuccess('Password reset email sent! Check your inbox.');
        setShowForgotPassword(false);
      } else {
        setError(result.error);
      }
    } catch (error) {
      setError(error.message || 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      confirmPassword: '',
      displayName: ''
    });
    setError('');
    setSuccess('');
    setShowForgotPassword(false);
  };

  const toggleMode = () => {
    setIsLogin(!isLogin);
    resetForm();
  };

  if (showForgotPassword) {
    return (
      <Card className="card-user">
        <CardHeader>
          <CardTitle tag="h4">Reset Password</CardTitle>
        </CardHeader>
        <CardBody>
          {error && <Alert color="danger" timeout={5000}>{error}</Alert>}
          {success && <Alert color="success" timeout={5000}>{success}</Alert>}
          
          <Form onSubmit={(e) => { e.preventDefault(); handleForgotPassword(); }}>
            <FormGroup>
              <Label>Email Address</Label>
              <Input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
              />
            </FormGroup>
            
            <div className="text-center">
              <Button
                color="primary"
                type="submit"
                disabled={loading}
                className="btn-round"
              >
                {loading ? <Spinner size="sm" /> : 'Send Reset Email'}
              </Button>
            </div>
          </Form>
          
          <div className="text-center mt-3">
            <Button
              color="link"
              onClick={() => setShowForgotPassword(false)}
            >
              Back to {isLogin ? 'Login' : 'Sign Up'}
            </Button>
          </div>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card className="card-user">
      <CardHeader>
        <CardTitle tag="h4">
          {isLogin ? 'Sign In' : 'Create Account'}
        </CardTitle>
      </CardHeader>
      <CardBody>
        {error && <Alert color="danger" timeout={5000}>{error}</Alert>}
        {success && <Alert color="success" timeout={5000}>{success}</Alert>}

        {/* Google Sign In Button */}
        <div className="text-center mb-4">
          <Button
            color="danger"
            onClick={handleGoogleAuth}
            disabled={loading}
            className="btn-round"
            style={{ width: '100%' }}
          >
            {loading ? <Spinner size="sm" /> : 'Continue with Google'}
          </Button>
        </div>

        <div className="text-center mb-3">
          <span className="text-muted">or</span>
        </div>

        {/* Email/Password Form */}
        <Form onSubmit={handleEmailAuth}>
          {!isLogin && (
            <FormGroup>
              <Label>Full Name</Label>
              <Input
                type="text"
                name="displayName"
                value={formData.displayName}
                onChange={handleInputChange}
                placeholder="Enter your full name"
              />
            </FormGroup>
          )}

          <FormGroup>
            <Label>Email Address</Label>
            <Input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="Enter your email"
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>Password</Label>
            <Input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              placeholder="Enter your password"
              required
            />
          </FormGroup>

          {!isLogin && (
            <FormGroup>
              <Label>Confirm Password</Label>
              <Input
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                placeholder="Confirm your password"
                required
              />
            </FormGroup>
          )}

          <div className="text-center">
            <Button
              color="primary"
              type="submit"
              disabled={loading}
              className="btn-round"
              style={{ width: '100%' }}
            >
              {loading ? <Spinner size="sm" /> : (isLogin ? 'Sign In' : 'Create Account')}
            </Button>
          </div>
        </Form>

        {/* Additional Options */}
        <div className="text-center mt-3">
          {isLogin ? (
            <>
              <Button
                color="link"
                onClick={() => setShowForgotPassword(true)}
                className="p-0"
              >
                Forgot your password?
              </Button>
              <br />
              <Button
                color="link"
                onClick={toggleMode}
                className="p-0"
              >
                Don't have an account? Sign up
              </Button>
            </>
          ) : (
            <Button
              color="link"
              onClick={toggleMode}
              className="p-0"
            >
              Already have an account? Sign in
            </Button>
          )}
        </div>
      </CardBody>
    </Card>
  );
};

export default FirebaseAuth;
